import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import gibletLogo from '@/assets/giblet-logo.png';

const curtKBs = [
  { id: 'kb-safety-blueprint', name: 'Construction Safety Blueprint' },
  { id: 'kb-vision-mission', name: 'CURT Vision Mission Strategies' },
  { id: 'kb-owner-safety', name: 'CURT Owner Safety Blueprint' },
  { id: 'kb-mckinsey', name: 'McKinsey Delivering on Construction Productivity' },
  { id: 'kb-modularization', name: 'CURT Owners Blueprint for Modularization' },
  { id: 'kb-poc', name: "iSPAI's POC Deliverables" },
  { id: 'kb-transform-ai', name: 'iSPAI and CURT Transform AI' },
];

export default function DemoCallForDestructionEvidence() {
  const navigate = useNavigate();
  const location = useLocation();

  const [targetKB, setTargetKB] = useState('kb-safety-blueprint');
  const [contentLocation, setContentLocation] = useState('Section 3.2 — Personal Protective Equipment Standards');
  const [evidence, setEvidence] = useState(
    'Section 3.2 references 2018 OSHA standards (29 CFR 1926.95) for PPE requirements that were significantly updated in the 2024 OSHA Final Rule. The current KB content recommends PPE classifications that no longer meet minimum compliance thresholds. Specifically:\n\n1. Hard hat ratings reference ANSI Z89.1-2014, superseded by ANSI/ISEA Z89.1-2024\n2. Fall protection trigger heights cite the old 6-foot standard without noting the 2023 updated guidance\n3. Respiratory protection references do not include the 2024 APF table updates'
  );
  const [severity, setSeverity] = useState('high');

  const handleNext = () => {
    const kb = curtKBs.find(k => k.id === targetKB);
    navigate('/demo-call-for-destruction/bounty', {
      state: {
        ...location.state,
        targetKBId: targetKB,
        targetKBName: kb?.name,
        contentLocation,
        evidence,
        severity,
      }
    });
  };

  return (
    <div className="min-h-screen bg-background flex flex-col items-center justify-center p-6">
      <div className="max-w-2xl w-full space-y-8">
        <div className="flex justify-center mb-8">
          <img src={gibletLogo} alt="Giblet Logo" className="h-16 w-auto" />
        </div>

        <div className="text-center space-y-4">
          <h1 className="text-3xl font-bold text-foreground">Evidence Submission</h1>
          <p className="text-lg text-muted-foreground">
            Identify the target content and provide supporting evidence
          </p>
        </div>

        <div className="bg-card rounded-lg p-8 shadow-lg space-y-5">
          <div className="space-y-2">
            <Label>Target Knowledge Base</Label>
            <Select value={targetKB} onValueChange={setTargetKB}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {curtKBs.map((kb) => (
                  <SelectItem key={kb.id} value={kb.id}>{kb.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>Specific Content Location</Label>
            <Input value={contentLocation} onChange={(e) => setContentLocation(e.target.value)} placeholder="Section, page, or URL reference" />
          </div>

          <div className="space-y-2">
            <Label>Evidence Description</Label>
            <Textarea value={evidence} onChange={(e) => setEvidence(e.target.value)} rows={8} />
          </div>

          <div className="space-y-2">
            <Label>Severity Level</Label>
            <RadioGroup value={severity} onValueChange={setSeverity} className="grid grid-cols-2 gap-2">
              {[
                { value: 'critical', label: 'Critical', desc: 'Immediate safety or legal risk' },
                { value: 'high', label: 'High', desc: 'Significant inaccuracy or violation' },
                { value: 'medium', label: 'Medium', desc: 'Outdated but not immediately harmful' },
                { value: 'low', label: 'Low', desc: 'Minor issue, update recommended' },
              ].map((s) => (
                <div key={s.value} className="flex items-start space-x-2 p-3 rounded-lg hover:bg-accent/50 transition-colors border border-border">
                  <RadioGroupItem value={s.value} id={`sev-${s.value}`} className="mt-0.5" />
                  <Label htmlFor={`sev-${s.value}`} className="cursor-pointer">
                    <div className="font-semibold text-foreground text-sm">{s.label}</div>
                    <div className="text-xs text-muted-foreground">{s.desc}</div>
                  </Label>
                </div>
              ))}
            </RadioGroup>
          </div>

          <div className="space-y-2">
            <Label>Supporting Documentation (optional)</Label>
            <div className="border-2 border-dashed border-border rounded-lg p-6 text-center text-muted-foreground text-sm">
              Drag & drop files here or click to browse
            </div>
          </div>
        </div>

        <div className="flex justify-center gap-4">
          <Button variant="outline" onClick={() => navigate('/demo-call-for-destruction/issue-type')} className="min-w-[120px]">
            Back
          </Button>
          <Button onClick={handleNext} disabled={!targetKB || !evidence} className="min-w-[120px]">
            Next
          </Button>
        </div>
      </div>
    </div>
  );
}
